import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  billingConfigService,
  CreateBillingConfigPayload,
  UpdateBillingConfigPayload,
} from '../services/billingConfigService';
import { queryKeys } from '../lib/queryClient';

export const useBillingConfigs = (clientId: number | null) =>
  useQuery({
    queryKey: queryKeys.billingConfigs.active(clientId!),
    queryFn: () => billingConfigService.getActiveConfigs(clientId!),
    enabled: clientId != null,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

export const useBillingConfigHistory = (clientId: number | null) =>
  useQuery({
    queryKey: queryKeys.billingConfigs.history(clientId!),
    queryFn: () => billingConfigService.getConfigHistory(clientId!),
    enabled: clientId != null,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

export const useCreateBillingConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateBillingConfigPayload) =>
      billingConfigService.createConfig(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.billingConfigs.active(variables.clientId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.billingConfigs.history(variables.clientId),
      });
    },
  });
};

export const useUpdateBillingConfig = (configId: number, clientId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateBillingConfigPayload) =>
      billingConfigService.updateConfig(configId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.billingConfigs.active(clientId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.billingConfigs.history(clientId),
      });
    },
  });
};
